/**
 * [REDE] Escolha da unidade — primeira tela de quem abre o link de uma rede
 * com cardápio único.
 *
 * Memória: `.claude/.../memory/project_rede_cardapio_por_unidade.md`
 *
 * Por que existe (dono, 15/09/2026): o link publicado era sempre o da loja
 * vitrine, então todo cliente navegava no catálogo DELA. O lojista não
 * conseguia desativar um item só numa unidade (a tela do cliente nem olhava
 * pra aquela loja) nem cadastrar item exclusivo. Agora o cliente escolhe a
 * unidade e cai no `/app/<slug>` dela — e o `/api/menu/<slug>`, que já era por
 * loja, passa a mostrar o cardápio de verdade daquela unidade.
 *
 * Regras combinadas com o dono:
 *  - A lista SEMPRE aparece (não é um "permita a localização pra continuar").
 *    Localização é opcional e serve só pra ORDENAR e marcar a mais próxima —
 *    quem negar escolhe na mão e segue o baile.
 *  - A escolha fica lembrada no aparelho. Quem já escolheu abre direto no
 *    cardápio, com "trocar unidade" no cabeçalho.
 *  - Mesa/comanda NUNCA vê esta tela: o cliente está fisicamente na loja, a
 *    unidade é aquela e ponto.
 *
 * A distância é calculada AQUI, com as coordenadas que já vieram no HTML.
 * Não existe round-trip nem chave do Google envolvida — ordenar uma lista de
 * 2-5 lojas não justifica nenhum dos dois.
 */
import { useEffect, useState } from 'react';
import { t, useI18n } from '../i18n';
import { esquecerUnidade, lembrarUnidade, unidadeLembrada } from '../utils/redeUnidade';
import { MapaUnidade } from './MapaUnidade';

export interface NetworkUnit {
  id: number;
  slug: string;
  name: string;
  address: string;
  neighborhood: string;
  city: string;
  logo: string;
  is_open: boolean;
  delivery_estimate: string;
  lat: number | null;
  lng: number | null;
  is_entry: boolean;
}

declare global {
  interface Window {
    NETWORK_UNITS?: NetworkUnit[];
    NETWORK_GROUP_ID?: string;
  }
}

/** Distância em km entre dois pontos (Haversine). Boa o bastante pra ordenar
 *  lojas da mesma cidade — não é rota de carro, é linha reta. */
function distanciaKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const rad = (g: number) => (g * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function fmtKm(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1).replace('.', ',')} km`;
}

type Estado = 'parado' | 'buscando' | 'ok' | 'negado';

/** Cartão de uma unidade. O cartão INTEIRO não é clicável de propósito: com
 *  "Ver no mapa" do lado, um toque solto no corpo escolheria a loja errada. */
function CartaoUnidade({
  u, km, maisProxima, onEscolher, cliente,
}: {
  u: NetworkUnit;
  km: number | null;
  maisProxima: boolean;
  onEscolher: () => void;
  /** Posição do cliente — vira o 2º pino no mapa, pra ele comparar. */
  cliente: { lat: number; lng: number } | null;
}) {
  const [mapaAberto, setMapaAberto] = useState(false);
  const temCoord = u.lat != null && u.lng != null;

  return (
    <div
      className={[
        'bg-white rounded-2xl border overflow-hidden transition',
        maisProxima ? 'border-red-300 ring-2 ring-red-100 shadow-md' : 'border-gray-200 shadow-sm',
      ].join(' ')}
    >
      <div className="p-4 flex items-start gap-3">
        {u.logo ? (
          <img src={u.logo} alt="" className="w-14 h-14 rounded-xl object-contain bg-white border border-gray-100 shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
            <i className="fas fa-store text-lg" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-gray-900 text-[15px] leading-tight">{u.name}</span>
            {maisProxima && (
              <span className="text-[10px] font-extrabold text-white bg-red-600 px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
                {t('Mais próxima')}
              </span>
            )}
          </div>
          {u.address && (
            <p className="text-xs text-gray-500 mt-1 leading-snug">{u.address}</p>
          )}
          <div className="flex items-center gap-2.5 mt-2 flex-wrap text-[11px] font-bold">
            <span className={u.is_open ? 'text-green-700' : 'text-red-600'}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${u.is_open ? 'bg-green-500' : 'bg-red-500'}`} />
              {u.is_open ? t('Loja Aberta') : t('Fechados no momento')}
            </span>
            {km != null && (
              <span className="text-gray-500"><i className="fas fa-location-dot mr-1 text-gray-300" />{fmtKm(km)}</span>
            )}
            {u.delivery_estimate && (
              <span className="text-gray-400"><i className="far fa-clock mr-1" />{u.delivery_estimate}</span>
            )}
          </div>
        </div>
      </div>

      {/* Mapa sob demanda: o iframe só entra no DOM quando o cliente pede.
          Montar 2-5 mapas do Google de uma vez na abertura seria caro e
          lento no celular, e a maioria escolhe sem olhar nenhum. */}
      {mapaAberto && temCoord && (
        <div className="px-4 pb-3">
          <MapaUnidade lat={u.lat as number} lng={u.lng as number} nome={u.name} cliente={cliente} />
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${u.lat},${u.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-700"
          >
            <i className="fas fa-diamond-turn-right" /> {t('Como chegar')}
          </a>
        </div>
      )}

      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMapaAberto((v) => !v)}
          disabled={!temCoord}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-100 transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <i className={`fas ${mapaAberto ? 'fa-chevron-up' : 'fa-map-location-dot'} text-gray-400`} />
          {mapaAberto ? t('Fechar mapa') : t('Ver no mapa')}
        </button>
        <button
          type="button"
          onClick={onEscolher}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xs font-bold shadow-sm transition active:scale-95"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {t('Escolher essa')} <i className="fas fa-arrow-right text-[10px]" />
        </button>
      </div>
    </div>
  );
}

export function NetworkUnitPickerIsland({
  unidades,
  onEscolher,
  onFechar,
}: {
  unidades: NetworkUnit[];
  onEscolher: (u: NetworkUnit) => void;
  /** Só existe quando a tela foi aberta por "trocar unidade" — na 1ª visita
   *  não há o que fechar, a escolha é obrigatória pra ver o cardápio. */
  onFechar?: () => void;
}) {
  useI18n();
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);
  const [estado, setEstado] = useState<Estado>('parado');

  // Pede a localização sozinho na abertura. O navegador só mostra o diálogo
  // uma vez por origem; quem já negou antes cai direto em 'negado' e vê a
  // lista normal, sem popup nenhum.
  useEffect(() => {
    pedirLocalizacao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pedirLocalizacao() {
    if (!navigator.geolocation) { setEstado('negado'); return; }
    setEstado('buscando');
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setEstado('ok'); },
      () => setEstado('negado'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  }

  // Ordena por distância quando há posição. Sem posição, a ordem é a que veio
  // do servidor (loja do link primeiro) — nunca aleatória.
  const lista = unidades.map((u) => ({
    u,
    km: pos && u.lat != null && u.lng != null
      ? distanciaKm(pos.lat, pos.lng, u.lat, u.lng)
      : null,
  }));
  if (pos) {
    // Loja sem coordenada vai pro fim: não dá pra medir, mas some seria pior.
    lista.sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity));
  }
  const idxMaisProxima = pos ? lista.findIndex((x) => x.km != null) : -1;
  const logoDaRede = unidades.find((u) => u.logo)?.logo || '';

  return (
    <div className="fixed inset-0 z-[120] bg-gray-50 overflow-y-auto">
      <div className="max-w-[560px] mx-auto min-h-full flex flex-col px-4">
        {onFechar && (
          <div className="pt-4">
            <button
              type="button"
              onClick={onFechar}
              aria-label={t('Fechar')}
              className="-ml-1 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 transition"
            >
              <i className="fas fa-arrow-left" />
            </button>
          </div>
        )}

        {/* Bloco do meio centralizado na vertical (`my-auto`): com 2-3
            unidades a lista é curta e, ancorada no topo, deixava metade da
            tela do PC em branco. */}
        <div className="my-auto">
        {/* Cabeçalho centralizado: numa tela larga o texto encostado na
            esquerda de um bloco centralizado fica torto. */}
        <div className={`text-center ${onFechar ? 'pt-4' : 'pt-10'} pb-6 shrink-0`}>
          {logoDaRede && (
            <img
              src={logoDaRede}
              alt=""
              className="w-20 h-20 rounded-2xl object-contain bg-white border border-gray-200 shadow-sm mx-auto mb-4 p-1"
            />
          )}
          <h1 className="text-2xl font-black text-gray-900 leading-tight">
            {t('Escolha a unidade')}
          </h1>
          <p className="text-sm text-gray-500 mt-2 leading-snug max-w-[420px] mx-auto">
            {t('Cada unidade tem seu próprio cardápio e sua área de entrega.')}
          </p>

          {estado === 'buscando' && (
            <p className="mt-4 text-xs font-bold text-gray-400 flex items-center justify-center gap-2">
              <i className="fas fa-circle-notch fa-spin" /> {t('Procurando a unidade mais perto de você…')}
            </p>
          )}
          {estado === 'negado' && (
            <button
              type="button"
              onClick={pedirLocalizacao}
              className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-xs font-bold text-gray-600 hover:border-red-300 transition"
            >
              <i className="fas fa-location-crosshairs text-red-500" />
              {t('Usar minha localização pra achar a mais perto')}
            </button>
          )}
        </div>

        <div className="space-y-3">
          {lista.map(({ u, km }, i) => (
            <CartaoUnidade
              key={u.id}
              u={u}
              km={km}
              cliente={pos}
              maisProxima={i === idxMaisProxima && km != null}
              onEscolher={() => onEscolher(u)}
            />
          ))}
        </div>

        </div>

        {/* Selo da plataforma. A tela tem pouco conteúdo por natureza (2-4
            unidades) e terminava num vazio grande; o selo fecha a página e
            é onde a marca aparece pro cliente final. */}
        <div className="pt-10 pb-8 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px bg-gray-200 flex-1" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300">
              {t('Tecnologia')}
            </span>
            <div className="h-px bg-gray-200 flex-1" />
          </div>
          {/* SELO, NÃO ANÚNCIO. Sem link e sem frase de venda, de propósito:
              esta tela é do cliente da LOJA. Mandar ele pra alivhub.com.br
              seria tirar o cliente do lojista de dentro do app dele pra uma
              página de vendas B2B — cliente da pizzaria não tem nada que
              fazer lá. Só a marca, discreta, como assinatura da plataforma.

              `logo-alivhub.png` é o `logo.png` SEM a moldura branca de 60%
              que o original tem — com ela, qualquer altura em CSS renderiza
              a marca ilegível. */}
          <div className="flex justify-center opacity-50">
            <img src="/static/logo-alivhub.png" alt="Aliv Hub" className="h-6 object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** A loja do link é uma das unidades da rede? (a tela só faz sentido se sim) */
export function ehRedeComEscolha(): boolean {
  const u = window.NETWORK_UNITS || [];
  return u.length >= 2;
}

export function unidadePorSlug(slug: string): NetworkUnit | undefined {
  return (window.NETWORK_UNITS || []).find((u) => u.slug === slug);
}

// Reexportado por conveniência: quem já importa a ilha não precisa saber
// que a memória da escolha mudou de arquivo.
export { esquecerUnidade, lembrarUnidade, unidadeLembrada };
